import {Lambda} from "@/simulation/Lambda";
import {ApiGateway} from "@/simulation/ApiGateway";
import {EventBridge} from "@/simulation/EventBridge";
import {ExternalApi} from "@/simulation/ExternalApi";
import {Sqs} from "@/simulation/Sqs";
import {AwsService} from "@/simulation/AwsService";

export abstract class Scenario {
    readonly name: string = '';
    services: Array<AwsService|Array<AwsService>> = [];
    rps = 0;
    rpsHandler?: number;

    reset() {
        this.services.flat().map((service) => service.reset());
    }

    invoke(count = 1): void {
        const service = this.services[0];

        for (let i = 0; i < count; i++) {
            if (service instanceof Array) {
                service.forEach((service) => service.invoke());
            } else {
                service.invoke();
            }
        }
    }

    getAllServices(): Array<AwsService> {
        return this.services.flat();
    }

    getInputServices(): Array<AwsService> {
        let inputServices = this.services[0];
        if (! (inputServices instanceof Array)) {
            inputServices = [inputServices];
        }
        return inputServices;
    }

    setRps(rps: number) {
        this.rps = rps;
        window.clearInterval(this.rpsHandler);
        const threshold = 20;
        if (rps > threshold) {
            this.rpsHandler = window.setInterval(() => {
                for (let i = 0; i < Math.round(rps / threshold); i++) {
                    this.invoke();
                }
            }, Math.round(1000 / threshold));
        } else if (rps > 0) {
            this.rpsHandler = window.setInterval(() => {
                this.invoke();
            }, Math.floor(1000 / rps));
        }
    }
}

export class ScenarioApi extends Scenario {
    name = 'API';
    constructor() {
        super();

        const httpLambda = new Lambda;
        this.services = [
            new ApiGateway(httpLambda),
            httpLambda,
        ];
    }
}

export class Scenario3rdPartyApi extends Scenario {
    name = 'API + 3rd party';
    constructor() {
        super();
        const httpLambda = new Lambda;

        const api = new ExternalApi;
        httpLambda.connectToApi(api);

        this.services = [
            new ApiGateway(httpLambda),
            httpLambda,
            api,
        ];
    }
}

export class ScenarioSqs extends Scenario {
    name = 'SQS';
    constructor() {
        super();
        this.services.push(new Sqs);
    }
}

export class ScenarioSqsLambda extends Scenario {
    name = 'SQS + Lambda';
    constructor() {
        super();
        const sqsLambda = new Lambda;

        const sqs = new Sqs;
        sqs.connectToLambda(sqsLambda);

        const api = new ExternalApi;
        sqsLambda.connectToApi(api);

        this.services.push(sqs);
        this.services.push(sqsLambda);
        this.services.push(api);
    }
}

export class ScenarioDlq extends Scenario {
    name = 'DLQ';
    constructor() {
        super();
        const sqsLambda = new Lambda;

        const sqs = new Sqs;
        sqs.connectToLambda(sqsLambda);

        const dlq = new Sqs;
        dlq.name = 'DLQ (SQS)';
        sqs.connectToDlq(dlq);

        const api = new ExternalApi;
        sqsLambda.connectToApi(api);

        this.services.push(sqs);
        this.services.push([sqsLambda, dlq]);
        this.services.push(api);
    }
}

export class ScenarioApiSqs extends Scenario {
    name = 'API + SQS';
    constructor() {
        super();
        const httpLambda = new Lambda;

        const sqsLambda = new Lambda;
        sqsLambda.maxConcurrency = 3;

        const apiGateway = new ApiGateway(httpLambda);

        const sqs = new Sqs;
        httpLambda.connectToSqs(sqs);
        sqs.connectToLambda(sqsLambda);

        const api = new ExternalApi;
        sqsLambda.connectToApi(api);

        this.services.push(apiGateway);
        this.services.push(httpLambda);
        this.services.push(sqs);
        this.services.push(sqsLambda);
        this.services.push(api);
    }
}

export class ScenarioEventBridge extends Scenario {
    name = 'EventBridge';
    constructor() {
        super();
        const eventBridgeLambda1 = new Lambda;
        const eventBridgeLambda2 = new Lambda;

        const eventBridge = new EventBridge();
        eventBridge.connectToLambda(eventBridgeLambda1);
        eventBridge.connectToLambda(eventBridgeLambda2);

        const api1 = new ExternalApi;
        api1.name = 'Twitter';
        const api2 = new ExternalApi;
        api2.name = 'Facebook';
        eventBridgeLambda1.connectToApi(api1);
        eventBridgeLambda2.connectToApi(api2);

        this.services.push(eventBridge);
        this.services.push([eventBridgeLambda1, eventBridgeLambda2]);
        this.services.push([api1, api2]);
    }
}

export class ScenarioEventBridgeVsSqs extends Scenario {
    name = 'EventBridge vs SQS';
    constructor() {
        super();
        this.services.push([new Sqs, new EventBridge]);
    }
}

export class ScenarioEventBridgeVsSqsAndLambda extends Scenario {
    name = 'EventBridge vs SQS + Lambda';
    constructor() {
        super();
        const sqsLambda = new Lambda;

        const sqs = new Sqs;
        sqs.connectToLambda(sqsLambda);

        const eventBridgeLambda = new Lambda;

        const eventBridge = new EventBridge;
        eventBridge.connectToLambda(eventBridgeLambda);

        this.services.push([sqs, eventBridge]);
        this.services.push([sqsLambda, eventBridgeLambda]);
    }
}

export class ScenarioApiEventBridge extends Scenario {
    name = 'API + EventBridge';
    constructor() {
        super();
        const httpLambda = new Lambda;

        const eventBridgeLambda1 = new Lambda;
        const eventBridgeLambda2 = new Lambda;
        eventBridgeLambda2.maxConcurrency = 3;

        const apiGateway = new ApiGateway(httpLambda);

        const eventBridge = new EventBridge();
        httpLambda.connectToEventBridge(eventBridge);
        eventBridge.connectToLambda(eventBridgeLambda1);
        eventBridge.connectToLambda(eventBridgeLambda2);

        const api1 = new ExternalApi;
        eventBridgeLambda1.connectToApi(api1);
        const api2 = new ExternalApi;
        eventBridgeLambda2.connectToApi(api2);

        this.services.push(apiGateway);
        this.services.push(httpLambda);
        this.services.push(eventBridge);
        this.services.push([eventBridgeLambda1, eventBridgeLambda2]);
        this.services.push([api1, api2]);
    }
}
